import { DateTime } from "luxon"
import { EventDropArg } from "@fullcalendar/core"
import { EventResizeDoneArg } from "@fullcalendar/interaction"

import { DateTimeFactory } from "../../../../../../modules/localization/useDateTime"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { DeliveryWithCart } from "../../form/formTypes"
import { earliestDeliveryTime } from "../../shop/delivery/helpers"
import { isAllDayAvailabilitySlot } from "../calendar/helpers"

import { TimelineCalendarEvent, TimelineCalendarEventTimeRange } from "../calendar/fullCalendarTypes"

export const LOCAL_EVENT = "local-"
const LOCAL_EVENT_COLOR = tailwindPalette.blue[300]

const EVENT_COLOR_OUT_OF_SERVICE = tailwindPalette.slate[300]
const EVENT_COLOR_CAN_EDIT = "#80CBC4"
const EVENT_COLOR_DEFAULT = tailwindPalette.red[300]

export function getEventBackgroundColor(event: { outOfService?: boolean; canEdit?: boolean }): string {
    if (event.outOfService) return EVENT_COLOR_OUT_OF_SERVICE
    if (event.canEdit) return EVENT_COLOR_CAN_EDIT
    return EVENT_COLOR_DEFAULT
}

export const DEFAULT_DURATION_MINUTES = 30
export const DEFAULT_MINIMUM_DURATION_MINUTES = 5

/**
 * Earliest end time a meeting that has already started may be shortened to:
 * the current time plus the minimum booking step.
 */
export function getMinAllowedEndTime(dateTimeFactory: DateTimeFactory): DateTime {
    return dateTimeFactory.now().plus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES })
}

export const isLocalEvent = (id: string) => id?.startsWith(LOCAL_EVENT)
export const toEventSourceId = (serviceCenterId: string, dayOffset: number) =>
    `source-${serviceCenterId}-day${dayOffset}`
export const isCanEditExistOrder = (info: EventDropArg | EventResizeDoneArg) => !!info.event?.extendedProps["canEdit"]

export const createNewEvent = (
    resourceId: string,
    start: string,
    end: string,
    timeRange?: TimelineCalendarEventTimeRange,
): TimelineCalendarEvent => ({
    id: `${LOCAL_EVENT}${resourceId}-${start}`,
    resourceIds: [resourceId],
    start,
    end,
    backgroundColor: LOCAL_EVENT_COLOR,
    timeRange,
})

export const createInitEvents = (resourceIds: string[], start: string, end: string): TimelineCalendarEvent => {
    const firstResourceId = resourceIds[0] || "book-period"

    return {
        id: `${LOCAL_EVENT}${firstResourceId}-${start}`,
        resourceIds,
        start,
        end,
        backgroundColor: LOCAL_EVENT_COLOR,
    }
}

export function roundDateTimeToNearestMinutes(dateTime: DateTime, nearestMinutes: number): DateTime {
    const rounded = Math.ceil(dateTime.minute / nearestMinutes) * nearestMinutes
    return dateTime.set({ minute: rounded, second: 0, millisecond: 0 })
}

export const BOOKING_ROUND_MINUTES = 5

/**
 * Rounds a DateTime UP to the nearest `intervalMinutes` boundary.
 * Handles seconds/milliseconds by first ceiling to the next whole minute.
 * Correctly overflows into the next hour/day via Luxon's `plus`.
 */
export function ceilDateTimeToInterval(dt: DateTime, intervalMinutes: number): DateTime {
    const base = dt.second !== 0 || dt.millisecond !== 0 ? dt.startOf("minute").plus({ minutes: 1 }) : dt
    const remainder = base.minute % intervalMinutes
    if (remainder === 0) return base.set({ second: 0, millisecond: 0 })
    return base.plus({ minutes: intervalMinutes - remainder }).set({ second: 0, millisecond: 0 })
}

/**
 * Tries to round the end time UP to the nearest `roundToMinutes` boundary.
 * Falls back to the original end if the rounded time would overlap another event (including buffers).
 */
export function tryRoundEndTime(
    args: Readonly<{
        start: DateTime
        end: DateTime
        roundToMinutes: number
        isAvailable: (startISO: string, endISO: string) => boolean
        toISO: (dt: DateTime) => string
    }>,
): DateTime {
    const { start, end, roundToMinutes, isAvailable, toISO } = args
    const rounded = ceilDateTimeToInterval(end, roundToMinutes)
    if (rounded.valueOf() <= end.valueOf()) return end
    if (isAvailable(toISO(start), toISO(rounded))) return rounded
    return end
}

/**
 * Rounds a DateTime to the nearest `intervalMinutes` boundary.
 * Rounds down if remainder < half-interval, up otherwise.
 */
export function roundToNearestInterval(dt: DateTime, intervalMinutes: number): DateTime {
    const remainder = dt.minute % intervalMinutes
    if (remainder === 0) return dt.set({ second: 0, millisecond: 0 })
    return remainder < intervalMinutes / 2
        ? dt.minus({ minutes: remainder }).set({ second: 0, millisecond: 0 })
        : dt.plus({ minutes: intervalMinutes - remainder }).set({ second: 0, millisecond: 0 })
}

/**
 * Rounds a DateTime DOWN to the nearest `intervalMinutes` boundary.
 */
export function floorDateTimeToInterval(dt: DateTime, intervalMinutes: number): DateTime {
    const remainder = dt.minute % intervalMinutes
    if (remainder === 0) return dt.set({ second: 0, millisecond: 0 })
    return dt.minus({ minutes: remainder }).set({ second: 0, millisecond: 0 })
}

/**
 * Where a booking that should begin "now" actually starts: the next full minute. Minute precision
 * (instead of the 5-minute booking grid) keeps the block on the now-indicator — at 13:47 the meeting
 * starts at 13:48 — while never storing a start in the past. New bookings only.
 */
export function getEarliestBookableStart(dateTimeFactory: DateTimeFactory): DateTime {
    return ceilToMinute(dateTimeFactory.now())
}

/**
 * The earliest point the user may aim at while dragging/selecting a new booking: the minute that
 * contains the now-indicator. The commit handlers pull such a start up to `getEarliestBookableStart`.
 */
export function getEarliestSelectableStart(dateTimeFactory: DateTimeFactory): DateTime {
    return dateTimeFactory.now().startOf("minute")
}

/**
 * Whether a start falls inside the booking-grid cell that holds the now-indicator. A new booking keeps
 * minute precision there, so it can sit right on the line instead of being rounded to the next
 * 5-minute boundary; further away the usual grid rounding applies.
 */
export function isWithinNowBookingCell(start: DateTime, dateTimeFactory: DateTimeFactory): boolean {
    return start < ceilDateTimeToInterval(dateTimeFactory.now(), BOOKING_ROUND_MINUTES)
}

/**
 * Tries to round the start time DOWN to the nearest `roundToMinutes` boundary.
 * Falls back to the original start if the rounded time would overlap another event (including buffers).
 */
export function tryRoundStartTime(
    args: Readonly<{
        start: DateTime
        end: DateTime
        roundToMinutes: number
        isAvailable: (startISO: string, endISO: string) => boolean
        toISO: (dt: DateTime) => string
    }>,
): DateTime {
    const { start, end, roundToMinutes, isAvailable, toISO } = args
    const rounded = floorDateTimeToInterval(start, roundToMinutes)
    if (rounded.valueOf() >= start.valueOf()) return start
    if (isAvailable(toISO(rounded), toISO(end))) return rounded
    return start
}

function ceilToMinute(dt: DateTime): DateTime {
    return dt.second !== 0 || dt.millisecond !== 0 ? dt.startOf("minute").plus({ minutes: 1 }) : dt
}

function roundUpNowForBooking(now: DateTime, intervalMinutes: number): DateTime {
    const roundedNowBase = ceilToMinute(now)
    const remainder = roundedNowBase.minute % intervalMinutes
    if (remainder === 0) return roundedNowBase.set({ second: 0, millisecond: 0 })
    return roundedNowBase.plus({ minutes: intervalMinutes - remainder }).set({ second: 0, millisecond: 0 })
}

export function getBookingRangeFromAvailabilitySlot(
    args: Readonly<{
        dateTimeFactory: DateTimeFactory
        slotStart: DateTime
        slotEnd: DateTime
        now: DateTime
        intervalMinutes: number
        allDayStartHour: number
        maxDurationHours: number
    }>,
): { effectiveStart: DateTime; adjustedEnd: DateTime; originalEnd: DateTime } | null {
    const { dateTimeFactory, slotStart, slotEnd, now, intervalMinutes, allDayStartHour, maxDurationHours } = args

    if (!slotStart.isValid || !slotEnd.isValid) return null
    if (!(slotEnd > slotStart)) return null

    // Slot is entirely in the past -> do not allow booking.
    if (slotEnd <= now) return null

    const roundedNow = roundUpNowForBooking(now, intervalMinutes)

    const startBase = isAllDayAvailabilitySlot(dateTimeFactory, slotStart, slotEnd)
        ? slotStart.set({ hour: allDayStartHour, minute: 0, second: 0, millisecond: 0 })
        : slotStart

    let effectiveStart = startBase < now ? roundedNow : startBase

    // If grid-aligned start meets or exceeds slot end, fall back to current minute so that
    // short remaining windows (≥ DEFAULT_MINIMUM_DURATION_MINUTES) are still bookable.
    if (effectiveStart >= slotEnd) {
        effectiveStart = ceilToMinute(now)
    }

    if (effectiveStart >= slotEnd) return null
    if (slotEnd.diff(effectiveStart, "minutes").minutes < DEFAULT_MINIMUM_DURATION_MINUTES) return null

    const desiredEnd = effectiveStart.plus({ hours: maxDurationHours })
    const adjustedEnd = desiredEnd < slotEnd ? desiredEnd : slotEnd

    return { effectiveStart, adjustedEnd, originalEnd: slotEnd }
}

export function earliestTime(times: DeliveryWithCart[]): string {
    return earliestDeliveryTime(times) ?? ""
}
